import { sb } from './supabase-client.js';
import { state } from './state.js';
import { paintScene } from './paint-engine.js';

/* ═══════════════════════════════════════════
   § 6. SUPABASE LIFECYCLE
   fetchData() — fetches all five tables in
   parallel and rebuilds CRM state.
   Callbacks are passed in by main.js to avoid
   coupling this module to ui-logic / nearby-locations.
   ═══════════════════════════════════════════ */

export async function fetchData(active_project, init, { renderCard, loadLocations }) {
  try {
    const [pR, uR, sR, stR, fR] = await Promise.all([
      sb.from('plots').select('*').eq('project_id', active_project),
      sb.from('ui_settings').select('*').eq('project_id', active_project).single(),
      sb.from('sectors').select('*').eq('project_id', active_project),
      sb.from('statuses').select('*'),
      sb.from('facings').select('*')
    ]);
    if (pR.error) throw pR.error;

    state.uiSettings = uR.data || {};
    const bName = state.uiSettings.brand_name || 'PlotOs';
    const bLogo = state.uiSettings.brand_logo_url;
    const bc  = document.getElementById('brand-container');
    const lbc = document.getElementById('loader-brand-container');

    if (bLogo) {
      if (bc)  bc.innerHTML  = `<img src="${bLogo}" alt="${bName}" style="max-height:28px; object-fit:contain; display:block;">`;
      if (lbc) lbc.innerHTML = `<img src="${bLogo}" alt="${bName}" style="max-height:40px; object-fit:contain; display:block;">`;
    } else {
      if (bc)  bc.textContent  = bName.toUpperCase();
      if (lbc) lbc.innerHTML   = bName.toUpperCase();
    }

    if (state.uiSettings.google_map_url) {
      const mapIframe = document.getElementById('googleMapIframe');
      if (mapIframe && mapIframe.src !== state.uiSettings.google_map_url) {
        mapIframe.src = state.uiSettings.google_map_url;
      }
    }

    try {
      state.config = typeof state.uiSettings.settings === 'string'
        ? JSON.parse(state.uiSettings.settings)
        : (state.uiSettings.settings || {});
    } catch (e) {
      state.config = {};
    }

    if (sR.data) sR.data.forEach(r => {
      state.sectorMap[r.id] = r.name || r.sector_name;
      if (r.color) state.sectorColorMap[r.id] = r.color; // Dynamic color from DB
    });
    if (stR.data) stR.data.forEach(r => { state.statusMap[r.id] = r.name || r.status_name; });
    if (fR.data)  fR.data.forEach(r => { state.facingMap[r.id]  = r.name || r.facing_name; });

    if (!Object.keys(state.statusMap).length) Object.assign(state.statusMap, { 1: 'Available', 2: 'Booked', 3: 'Sold' });
    if (!Object.keys(state.sectorMap).length) Object.assign(state.sectorMap, { 1: 'Commercial', 2: 'Sector_A', 3: 'Sector_B', 4: 'Residential' });
    if (!Object.keys(state.facingMap).length) Object.assign(state.facingMap, { 1: 'North', 2: 'South', 3: 'East', 4: 'West' });

    // Dynamic page title
    const projectName = state.uiSettings.project_name || state.uiSettings.brand_name || active_project;
    document.title = `${projectName} | Powered by PlotOs`;

    // Dynamic WhatsApp number (strip '+' prefix for wa.me)
    if (state.uiSettings.whatsapp_number) {
      state.waNumber = state.uiSettings.whatsapp_number.replace(/^\+/, '').replace(/\s/g, '');
    }

    // Auto-build sector legend from database
    const legSecEl = document.getElementById('legSec');
    if (legSecEl && sR.data && sR.data.length > 0) {
      legSecEl.innerHTML = sR.data.map(s => {
        const color = s.color || '#888';
        const name  = (s.name || s.sector_name || 'Unknown').replace('_', ' ').toUpperCase();
        return `<div class="leg-i"><div class="leg-c" style="background:${color}"></div>${name}</div>`;
      }).join('');
    }

    if (init) {
      document.getElementById('sync').textContent = 'LIVE';
      document.getElementById('sync').style.cssText = 'border-color:#4ee661;color:#4ee661';
      loadLocations();
    }

    state.crmData = pR.data.map(r => ({
      ...r,
      Status:  state.statusMap[r.status_id] || 'Unknown',
      Facing:  state.facingMap[r.facing_id]  || 'Unknown',
      Sector:  state.sectorMap[r.sector_id]  || 'Unknown',
      Plot_ID: r.plot_id,
      MatName: r.plot_id ? r.plot_id.replace('Plot-', 'MAT_PLOT_') : null,
      Area:    r.area,
      Price:   r.price
    }));

    paintScene();
    if (state.selectedPlotId) renderCard(state.selectedPlotId);

  } catch (e) {
    console.error('[Supabase Sync Error]', e);
    document.getElementById('sync').textContent   = 'OFFLINE';
    document.getElementById('sync').style.cssText = 'border-color:#ef4444;color:#ef4444';
  }
}

export function startPolling(active_project, callbacks) {
  let _pollId = setInterval(() => fetchData(active_project, false, callbacks), 10000);

  // Pause polling when tab is hidden (iOS Safari kills background network tabs)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearInterval(_pollId);
      _pollId = null;
    } else if (!_pollId) {
      // Resume immediately + restart interval
      fetchData(active_project, false, callbacks).catch(() => {});
      _pollId = setInterval(() => fetchData(active_project, false, callbacks), 10000);
    }
  });
}
